import React, { useEffect, useState } from "react";
import { recordView } from "@/lib/recent";
import { useParams, Link } from "react-router-dom";
import { MapPin, BadgeCheck, ChevronLeft, Check } from "lucide-react";
import { listingsApi } from "@/lib/api";
import { StarRating } from "@/components/StarRating";
import { BookingWidget } from "@/components/BookingWidget";
import { Skeleton } from "@/components/ui/skeleton";
import { worldOf } from "@/lib/worlds";
import { prettyDate } from "@/lib/format";
import { ListingOffers } from "@/pages/account/Money";
import { ListingPosts } from "@/pages/stage/Social";
import { RentalRequest } from "@/pages/site/More";

function Gallery({ images = [], title }) {
  const [active, setActive] = useState(0);
  if (!images.length) return <div className="aspect-[16/10] rounded-2xl bg-muted" />;
  return (
    <div>
      <div className="aspect-[16/10] overflow-hidden rounded-2xl bg-muted">
        <img src={images[active]} alt={title} className="h-full w-full object-cover" />
      </div>
      {images.length > 1 && (
        <div className="no-scrollbar mt-3 flex gap-2 overflow-x-auto">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              data-testid={`gallery-thumb-${i}`}
              className={`h-16 w-20 shrink-0 overflow-hidden rounded-lg border-2 ${i === active ? "border-primary" : "border-transparent"}`}
            >
              <img src={img} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ListingDetail() {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => { if (listing && listing.id) recordView({ type: "listing", id: listing.id, title: listing.title, image: listing.image_url || (listing.images || [])[0], url: "/listing/" + listing.id }); }, [listing]);

  useEffect(() => {
    window.scrollTo(0, 0);
    let active = true;
    setListing(null);
    setNotFound(false);
    listingsApi
      .get(id)
      .then((d) => {
        if (!active) return;
        setListing(d);
        if (Array.isArray(d.reviews) && d.reviews.length) setReviews(d.reviews);
        else listingsApi.reviews(id).then((r) => active && setReviews(Array.isArray(r) ? r : [])).catch(() => {});
      })
      .catch(() => active && setNotFound(true));
    return () => {
      active = false;
    };
  }, [id]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-serif-display text-2xl font-bold">Listing not found</h1>
        <Link to="/marketplace" className="mt-4 inline-block font-semibold text-primary hover:underline">
          Back to marketplace
        </Link>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Skeleton className="aspect-[16/10] rounded-2xl" />
            <Skeleton className="mt-6 h-8 w-2/3" />
            <Skeleton className="mt-3 h-24 w-full" />
          </div>
          <Skeleton className="h-96 rounded-2xl" />
        </div>
      </div>
    );
  }

  const world = worldOf(listing.module);
  const meta = listing.meta || {};
  const highlights = listing.highlights || meta.highlights || [];

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
      <Link to="/marketplace" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="detail-back">
        <ChevronLeft size={16} /> Back
      </Link>

      <div className="grid gap-8 lg:grid-cols-3">
        {/* Left */}
        <div className="lg:col-span-2">
          <Gallery images={listing.images?.length ? listing.images : [listing.image_url]} title={listing.title} />

          <div className="mt-6">
            <span
              className="rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize"
              style={{ background: world.accentSoft, color: world.accent }}
            >
              {world.name} · {listing.category}
            </span>
            <h1 className="mt-3 flex items-center gap-2 font-serif-display text-3xl font-bold" data-testid="detail-title">
              {listing.title}
              {listing.verified && <BadgeCheck size={22} style={{ color: "var(--fable)" }} />}
            </h1>
            {listing.subtitle && <p className="mt-1 text-muted-foreground">{listing.subtitle}</p>}
            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
              <StarRating rating={listing.rating} count={listing.review_count} size={16} />
              {listing.location && (
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <MapPin size={14} /> {listing.location}
                </span>
              )}
              <span className="text-muted-foreground">by <span className="font-medium text-foreground">{listing.provider_name}</span></span>
            </div>
          </div>

          {listing.description && (
            <div className="mt-6">
              <h2 className="text-lg font-bold">About</h2>
              <p className="mt-2 whitespace-pre-line text-[15px] leading-relaxed text-foreground/85">{listing.description}</p>
            </div>
          )}

          {highlights.length > 0 && (
            <div className="mt-6">
              <h2 className="text-lg font-bold">Highlights</h2>
              <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                {highlights.map((h, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <Check size={16} className="shrink-0 text-primary" /> {h}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(meta.group_size || meta.min_age || (meta.bring && meta.bring.length)) && (
            <div className="mt-6 flex flex-wrap gap-2">
              {meta.group_size && <span className="rounded-full bg-muted px-3 py-1.5 text-sm">{meta.group_size}</span>}
              {meta.min_age && <span className="rounded-full bg-muted px-3 py-1.5 text-sm">Age {meta.min_age}</span>}
              {(meta.bring || []).map((b, i) => (
                <span key={i} className="rounded-full bg-muted px-3 py-1.5 text-sm">Bring: {b}</span>
              ))}
            </div>
          )}

          <ListingOffers listingId={listing.id} />
          <ListingPosts listingId={listing.id} />
          {listing.module === "rentals" ? <RentalRequest listing={listing} /> : null}

          {/* Reviews */}
          <div className="mt-8">
            <h2 className="text-lg font-bold">Reviews {reviews.length > 0 && <span className="text-muted-foreground">({reviews.length})</span>}</h2>
            {reviews.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">No reviews yet.</p>
            ) : (
              <div className="mt-4 space-y-4">
                {reviews.slice(0, 8).map((r) => (
                  <div key={r.id} className="rounded-2xl border border-border p-4" data-testid={`review-${r.id}`}>
                    <div className="flex items-center gap-3">
                      {r.user_avatar ? (
                        <img src={r.user_avatar} alt="" className="h-9 w-9 rounded-full object-cover" />
                      ) : (
                        <div className="h-9 w-9 rounded-full bg-muted" />
                      )}
                      <div>
                        <p className="text-sm font-semibold">{r.user_name}</p>
                        <p className="text-xs text-muted-foreground">{prettyDate(r.created_at)}</p>
                      </div>
                      <div className="ml-auto">
                        <StarRating rating={r.rating} />
                      </div>
                    </div>
                    {r.text && <p className="mt-2.5 text-sm text-foreground/85">{r.text}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right — sticky booking */}
        <div>
          <div className="lg:sticky lg:top-20">
            <BookingWidget listing={listing} />
          </div>
        </div>
      </div>
    </div>
  );
}
